import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { contactInfo } from "../data";
import GalleryLightbox from "../components/GalleryLightbox";

export default function ServiceDetail({ slug }) {
  const [pageData, setPageData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    
    // Hent den specifikke ydelsesside fra CMS
    fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error("Kunne ikke hente ydelses-data");
        return res.json();
      })
      .then((data) => {
        setPageData(data);
        
        // Hent det tilknyttede galleri dynamisk baseret på gallery_slug
        const gSlug = data.gallery_slug;
        if (gSlug) {
          return fetch(`https://api.dev.auxo.dk/public/by-slug/da/${gSlug}`);
        }
        return null;
      })
      .then((res) => {
        if (!res) return null;
        if (!res.ok) throw new Error("Kunne ikke hente galleri-data");
        return res.json();
      })
      .then((gData) => {
        if (gData) setGalleryData(gData);
        setLoading(false);
      })
      .catch((err) => {
        console.error(`Fejl ved hentning af data for ${slug}:`, err);
        setError(true);
        setLoading(false);
      });
  }, [slug]);

  // Helpers
  const getVal = (field, defaultVal = "") => {
    if (!field) return defaultVal;
    if (typeof field === "object") {
      return field.da || Object.values(field)[0] || defaultVal;
    }
    return field;
  };

  const getResizedUrl = (url, width = 1200) => {
    if (!url) return "";
    const imgUrl = Array.isArray(url) ? url[0] : url;
    if (!imgUrl.startsWith("https://assets.auxodev.dk")) return imgUrl;
    return imgUrl.replace(
      "https://assets.auxodev.dk/",
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
    );
  };

  // Sæt metadata dynamisk
  useEffect(() => {
    if (pageData) {
      document.title = getVal(pageData.meta_title, `${getVal(pageData.title)} - Grønhøjauto v/Mads Kallerup`);
      
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.setAttribute("name", "description");
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute("content", getVal(pageData.meta_description, ""));
    }
  }, [pageData, slug]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-[120px]">
        <div className="text-center space-y-4">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-green" />
          <p className="text-stone-500 font-semibold">Indlæser ydelse...</p>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center space-y-6 pt-[120px]">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-red-600">
          <i className="fa-solid fa-triangle-exclamation text-2xl" />
        </div>
        <h1 className="text-2xl font-black text-brand-stone-dark">Ydelsen blev ikke fundet</h1>
        <p className="text-stone-600">
          Der skete en fejl under indlæsningen af denne ydelse, eller siden eksisterer ikke længere i vores system.
        </p>
        <Link
          to="/"
          className="inline-block rounded-xl bg-brand-green px-6 py-3 text-sm font-bold text-white shadow-md transition hover:bg-brand-green-light"
        >
          Gå til forsiden
        </Link>
      </div>
    );
  }

  const heroImg = getResizedUrl(
    pageData?.hero_image || "https://assets.auxodev.dk/assets/45330f91-18ad-4301-b0b9-f03458bae5a3/gronhojauto-hero.jpg",
    1600
  );

  return (
    <div className="pt-[72px] min-[1200px]:pt-[115px]">
      {/* Hero / Banner */}
      <section className="relative py-20 bg-stone-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImg}
            alt={getVal(pageData.title)}
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-900/40 to-stone-950/60" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center space-y-4">
          <span className="text-xs font-bold tracking-widest text-brand-sage uppercase">
            Ydelse og ekspertise
          </span>
          <h1 className="text-3xl font-black tracking-tight sm:text-4xl md:text-5xl text-white">
            {getVal(pageData.title)}
          </h1>
          <p className="mx-auto max-w-2xl text-base text-stone-200 md:text-lg font-medium">
            {getVal(pageData.subtitle)}
          </p>
        </div>
      </section>

      {/* Indhold Sektion */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-6">
          {/* Brødtekst fra CMS */}
          <div
            className="prose prose-stone max-w-none prose-p:text-stone-600 prose-p:leading-relaxed prose-p:text-base prose-li:text-stone-600 prose-strong:text-brand-stone-dark prose-headings:text-brand-stone-dark prose-headings:font-black space-y-6"
            dangerouslySetInnerHTML={{ __html: getVal(pageData.content) }}
          />
        </div>
      </section>

      {/* Galleri Sektion */}
      {galleryData && galleryData.images && galleryData.images.length > 0 && (
        <section className="py-16 bg-brand-stone-light border-t border-stone-200/60">
          <div className="mx-auto max-w-5xl px-6 space-y-8">
            <div className="text-center space-y-2">
              <span className="text-xs font-bold tracking-widest text-brand-sage uppercase">
                Billedgallerier
              </span>
              <h2 className="text-2xl font-black tracking-tight text-brand-stone-dark sm:text-3xl">
                {getVal(galleryData.title, "Billeder af arbejdet")}
              </h2>
              <p className="text-sm text-stone-500 max-w-xl mx-auto">
                {getVal(galleryData.description, "Klik på et billede for at se det i stor visning. Du kan swipe på mobil.")}
              </p>
            </div>

            {/* Lightbox Galleri */}
            <GalleryLightbox
              images={galleryData.images}
              title={getVal(galleryData.title, getVal(pageData.title))}
            />
          </div>
        </section>
      )}

      {/* CTA Sektion */}
      <section className="py-16 bg-brand-green text-white text-center space-y-6">
        <div className="mx-auto max-w-3xl px-6 space-y-4">
          <h2 className="text-2xl font-black tracking-tight sm:text-3xl">
            Skal du have booket tid til {getVal(pageData.title).toLowerCase()}?
          </h2>
          <p className="text-stone-200 max-w-xl mx-auto text-sm sm:text-base">
            Udfyld vores kontaktformular, eller ring direkte til Mads for at aftale en tid, der passer dig.
          </p>
          <div className="pt-4 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/kontakt"
              state={{ subject: getVal(pageData.title) }}
              className="rounded-xl bg-brand-orange px-6 py-3 text-sm font-bold text-white shadow-md transition hover:bg-brand-orange-dark cursor-pointer"
            >
              Book tid nu
            </Link>
            <a
              href={`tel:+45${contactInfo.phone.replace(/\s/g, "")}`}
              className="rounded-xl bg-white/10 border border-white/20 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/20"
            >
              Ring: {contactInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
